import { RefObject, useEffect } from 'react';

type AnyEvent = MouseEvent | TouchEvent;

/**
 *
 * @param btnRef - ref to btn that opens hint
 * @param hintRef - ref to hint content
 * @param handler - handler that closes hint
 *
 * - hint is opened:
 * 		1) click on hintContent - nothing happens;
 * 		2) click on btn - hint closes (btn toggle worked);
 * 		3) click outside - hint closes;
 *
 * - hint is closed:
 * 		1) click on btn - hint opens;
 * 		2) click outside - nothing happens;
 */

export const useCloseHintOutside = <T extends HTMLElement = HTMLElement>(
	btnRef: RefObject<T>,
	hintRef: RefObject<T>,
	handler: () => void
) => {
	useEffect(() => {
		function handleClickOutside(event: AnyEvent) {
			const target = event.target as Node;
			const btn = btnRef.current;
			const hint = hintRef.current;

			if (btn && !btn.contains(target) && hint && !hint.contains(target)) {
				handler();
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [btnRef, hintRef, handler]);
};
